import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { EiqButton, EiqSelect, EiqSpinner } from 'eiq-storybook';
import { withRouter } from 'react-router-dom';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

import { withJobContext } from 'src/utils/context/JobContext';
import { Popup } from 'src/components/Popup';
import { notification } from 'src/components/Notification';
import {
  NOT_YET_STARTED,
  IN_PROCESS,
  VERIFIER_REVIEW_PENDING,
  VERIFIER_REVIEW_UNDERWAY,
  REVIEWER_REVISION_REQUESTED,
  COMPLETED,
} from 'src/utils/data/job';
import { downloadAttachments } from 'src/utils/helpers';
import client from 'src/utils/api/client';
import { GET_OBJECT_PRE_SIGNED_URL } from 'src/graphql/attachment/query';
import { useUpdateSaqSubmissionRequested } from 'src/hooks/job';
import TimelineProgressBar from 'src/components/TimelineProgressBar';
import { toSimpleUtcDateString } from 'src/utils/format/date';
import s from 'src/components/JobForm/JobForm.styles';
import { getJobStatusLabel, actionButtonVisibility } from './utils';

const NewSaqStatusesRow = ({
  job,
  isVerifier,
  isReviewer,
  userIsAuthorizedExternalSaqUser,
  isAdmin,
  isOnlyExternalUser,
  onSetStatus,
  reloadJob,
  checkValidationErrors,
  isSubmitting,
  onManageSaqUserClick,
  isManageSaqUserTabVisible,
  hasUnverifiedSaqService,
  hasOpenComment,
  jobDatasheetResults,
  services,
  setShowError,
  isSavingDatasheetCommentStatus,
  isSaving,
  redirect,
}) => {
  const { t } = useTranslation();
  const { status, windowStartAt, windowEndAt } = job;
  const [showModal, setShowModal] = useState(false);
  const [modalDetails, setModalDetails] = useState(null);
  const [downloadOptions, setDownloadOptions] = useState([]);

  const {
    isSubmitButtonVisible,
    isStartButtonVisible,
    isRequestClarificationButtonVisible,
    isSubmitForReviewButtonVisible,
    isCompleteButtonVisible,
    isExportAttachmentsButtonVisible
  } = actionButtonVisibility(
    status, 
    userIsAuthorizedExternalSaqUser,
    isOnlyExternalUser,
    isVerifier, 
    isReviewer, 
    hasUnverifiedSaqService,
    isAdmin
  );
  const showError = (isOnlyExternalUser || isAdmin)  && job.status === IN_PROCESS;
  const getAttachmentData = () => {
    const attachments = { original: [], additional: [] };
    if ( jobDatasheetResults.length ) {
      jobDatasheetResults.forEach(datasheetResult => datasheetResult.data.forEach(v => {
        if (v.result && v.result.type === 'file') {
          attachments.original = [...attachments.original, ...v.result.value];
        }
        if (v.result && v.result.type === 'table') {
          const flattenedArray = v.result.rows.flat();
          flattenedArray.forEach(el => {
            if (el.type === 'file') {
              attachments.original = [...attachments.original, ...el.value];
            }
          });
        }
        if (v?.attachmentsMeta?.length) {
          attachments.additional = [...attachments.additional, ...v.attachmentsMeta];
        }
      }));
      return { original: attachments.original.flat(), additional: attachments.additional.flat() };
    }                                                 
    return attachments;
  };

  const exportAttachments = async () => {
    const getPreSignedUrl = async (id, realName) => {
      const response = await client.query({
        query: GET_OBJECT_PRE_SIGNED_URL,
        variables: realName ? {
          id,
          realName,
        } : { id },
      });
      return response.data.getObjectPreSignedUrl.url;
    };

    const dataWithBlobOriginal = await Promise.all(
      getAttachmentData()?.original.map(
        async (attachment) => {
          const url = await getPreSignedUrl(attachment.attId, attachment.name);
          const response = await fetch(url);
          const blob = await response.blob();
          return { ...attachment, blob };
        },
      ));

    const dataWithBlobAdditional = await Promise.all(
      getAttachmentData()?.additional.map(
        async (attachment) => {
          const url = await getPreSignedUrl(attachment.attId, attachment.name);
          const response = await fetch(url);
          const blob = await response.blob();
          return { ...attachment, blob };
        },
      ));

    const zip = new JSZip();
    const originalFolder = zip.folder('Original');
    const additionalFolder = zip.folder('Additional');

    dataWithBlobOriginal.forEach(d => {
      originalFolder.file(d.name, d.blob);
    });

    dataWithBlobAdditional.forEach(d => {
      additionalFolder.file(d.name, d.blob);
    });

    const content = await zip.generateAsync({ type: 'blob' });

    saveAs(content, 'SAQ external attachments');

    return true;
  };

  useEffect(() => {
    if (modalDetails) {
      setShowModal(true);
    }
  }, [modalDetails]);

  // manage saq user button
  const manageSAQUserButton = (
    (isManageSaqUserTabVisible ? (<EiqButton 
      variant='btn--outline-link'
      label={t('MANAGE_SAQ_USER')}
      onClick={() => onManageSaqUserClick('MANAGE_SAQ_USER')}
    />) : null)
  );

  useEffect(() =>{
    
    const attachmentsService = services.filter( service => !!service.service.currentVersion.attachmentsMeta);
    let attatchmentMetas = [];
    for(let i = 0; i < attachmentsService.length; i+=1){
      const attachmentMetaArr = attachmentsService[i].service.currentVersion.attachmentsMeta;
      attatchmentMetas = [...attatchmentMetas, ...attachmentMetaArr];
    }

    let options = [];
    if(attatchmentMetas.length > 0) {
      options = [{
        value: "Download all",
        label: t('DOWNLOAD_ALL'),
      }, ...attatchmentMetas.map(v => ({ value: v.name, label: v.name, ...v }))];
    }

    setDownloadOptions(options)
  }, [services]);

  const exportData = async (downloadAll = false, downloadFile = null) => {
    try {
      if (downloadAll) {
        const attachmentsData = downloadOptions.filter(item => !!item.attId).map(c=> {
          const obj = {actionReference : 'saq',...c}
          return obj;
        });
        await downloadAttachments(attachmentsData.flat(), {
          zipFileName: `SAQ instruction`,
          putExtra: false,
        }); 
      } else {        
        const getPreSignedUrl = async (id, realName) => {
          const response = await client.query({
            query: GET_OBJECT_PRE_SIGNED_URL,
            variables: realName ? {
              id,
              realName,
            } : { id },
          });
          return response.data.getObjectPreSignedUrl.url;
        };
        const url = await getPreSignedUrl(downloadFile?.attId, downloadFile?.name);
        const response = await fetch(url);
        const blob = await response.blob();
        await saveAs(blob, downloadFile?.name); 
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onDownloadClick = (option) => {
    exportData(option?.value === 'Download all', option);
  };

  const downloadDropdown = (
    (isManageSaqUserTabVisible && downloadOptions.length > 0 ? (
      <s.SaqDownloadDropdownWrapper><EiqSelect 
        id={'saqDownloadDropdown'}
        name="saqDownloadDropdown"
        placeholder={"Download"}
        hasChips={false}
        wrapperClass='SelectInputWrapper'
        options={downloadOptions}
        onChange={(e, option) => {onDownloadClick(option)}}
        isClearable={false}
      /></s.SaqDownloadDropdownWrapper>) : null)
  );

  const currentDate = moment.utc().startOf('day');
  const windowEndDateUtc = moment.utc(job.windowEndAt).startOf('day');
  const jobCompletedAtUtc = moment.utc(job.jobCompletedAt).startOf('day');
  const hasDelay = (job.status !== COMPLETED && (currentDate > windowEndDateUtc)) || (job.status === COMPLETED && (jobCompletedAtUtc > windowEndDateUtc));

  const getDaysDelay = () => {
    if (hasDelay) {
      return (job.status === COMPLETED ? jobCompletedAtUtc.diff(windowEndDateUtc, 'days') : currentDate.diff(windowEndDateUtc, 'days'));
    }

    return null;
  }
  const daysDelay = getDaysDelay();
  const completionDateReal = toSimpleUtcDateString(job.jobCompletedAt, 'D MMMM YYYY');

  const onUpdateSaqSubmissionRequested = useUpdateSaqSubmissionRequested();
  const callSaqSubmited = async () =>{
    const response = await onUpdateSaqSubmissionRequested({ jobId: job.id });
    setShowError(true);
  }

  const [submitClicked, setSubmitClicked] = useState(false);
  const onChangeJobStatus = async (newStatus) => {
    try {
      await onSetStatus({
        jobId: job.id,
        event: newStatus,
        time: new Date(),
        redirect: true
      });
      redirect(t('SUBMITED_JOB'));
    } catch(error) {
      notification.error(error);
    } finally {
      setShowModal(false);
      setSubmitClicked(false);
    }
  }

  // disable 'Submit for Review' and 'Complete' button
  const disableButton = (isSavingDatasheetCommentStatus || hasOpenComment);
  useEffect(() => {
    if (!isSaving && submitClicked) {
      onChangeJobStatus('verifier_review_pending');
    }
  }, [isSaving]);

  return (
    <>
      <s.NewSaqStatusesRowWrapper>
        <div className='d-flex'>
          <h2>{job.reportNumber}</h2>
          { windowStartAt && windowEndAt && (<TimelineProgressBar 
              startDate={windowStartAt} 
              completionDate={windowEndAt} 
              label={t(getJobStatusLabel(status))}
              startDateLabel={t('START')} 
              completionDateLabel={t('COMPLETE')}
              hasDelay={hasDelay}
              daysDelay={daysDelay}
              isCompleted={job.status === COMPLETED}
              completionDateReal={completionDateReal}
              isInProgress={job.status !== COMPLETED && job.status !== NOT_YET_STARTED}
              isDefaultState={job.status === NOT_YET_STARTED}
            />)
          }
        </div>
        <s.Status>
          { manageSAQUserButton }
          {downloadDropdown}
          {
            isExportAttachmentsButtonVisible && <EiqButton 
              label={t('EXPORT_ATTACHMENTS')}
              onClick={exportAttachments}
              variant="btn--outline-link"
              id="export-attachments"
            />
          }
          { isSubmitButtonVisible && <EiqButton label={t('SUBMIT')}
                                                onClick={() =>  {
              const hasErrors = showError ? checkValidationErrors() : false;
              if (!hasErrors) {
                setModalDetails({
                  title: t('SUBMIT_SAQ'),
                  messageFirstLine: t('SAQ_MODAL_MESSAGE_FIRST_LINE', {message: 'submitted for review'}),
                  buttonText: t('SUBMIT'),
                  setJobStatus: 'verifier_review_pending'
                });
              }else {
                callSaqSubmited();
              }
            }}
                                                disabled={isSubmitting}
            />
          }
          { isStartButtonVisible && 
            <EiqButton label={t('START')}
                       onClick={async () => {
              try {
                let changeStatus = 'reviewer_review_underway';
                if([VERIFIER_REVIEW_PENDING].includes(status))
                  changeStatus = 'verifier_review_underway'; 
                else if( [REVIEWER_REVISION_REQUESTED].includes(status)) 
                  changeStatus = 'verifier_revision_underway';
                await onSetStatus({
                  jobId: job.id,
                  event: changeStatus,
                  time: new Date(),
                });
                await reloadJob();
              } catch (e) {
                notification.error(e.graphQLErrors[0].message);
              }
            }}
            />
          }
          { isRequestClarificationButtonVisible && 
            <EiqButton label={t('REQUEST_CLARIFICATION')}
                       onClick={() => {
                setModalDetails({
                  title: t('REQUEST_CLARIFICATION'),
                  messageFirstLine: t('SAQ_MODAL_MESSAGE_FIRST_LINE', {message: 'submitted for clarification'}),
                  buttonText: t('SUBMIT'),
                  setJobStatus: status === VERIFIER_REVIEW_UNDERWAY ? 'verifier_revision_requested' : 'reviewer_revision_requested'
                });
              }}
                       variant={(isSavingDatasheetCommentStatus || !hasOpenComment) ? "btn--outline-link" : "btn--solid"}
                       disabled={isSavingDatasheetCommentStatus || !hasOpenComment}
            />
          }
          { isSubmitForReviewButtonVisible && 
            <EiqButton label={t('SUBMIT_FOR_REVIEW')}
                       onClick={() => {
                setModalDetails({
                  title: t('SUBMIT_SAQ'),
                  messageFirstLine: t('SAQ_MODAL_MESSAGE_FIRST_LINE', {message: 'submitted for review'}),
                  buttonText: t('SUBMIT'),
                  setJobStatus: 'reviewer_review_pending',
                });
              }}
                       variant={disableButton ? "btn--outline-link" : "btn--solid"}
                       disabled={disableButton}
            />
          }
          { isCompleteButtonVisible &&
            <EiqButton label={t('COMPLETE')}
                       onClick={() => {
                setModalDetails({
                  title: t('COMPLETE_SAQ'),
                  messageFirstLine: t('SAQ_MODAL_MESSAGE_FIRST_LINE', {message: 'completed'}),
                  buttonText: t('COMPLETE'),
                  setJobStatus: 'completed'
                });
              }
            }
                       variant={disableButton ? "btn--outline-link" : "btn--solid"}
                       disabled={disableButton}
            />
          }
        </s.Status>
      </s.NewSaqStatusesRowWrapper>
      {
      showModal && (<s.SaqConfirmationModalWrapper>
        <Popup
        customTitle={<s.Title className="modal-title">{modalDetails?.title}</s.Title>}
        withOverflow
        isCloseHidden
        className="small-container"
        >
        <div className="saq-modal-body">
          <p>{t(modalDetails?.messageFirstLine)}</p>
          <p>{t('SAQ_MODAL_MESSAGE_SECOND_LINE')}</p>
        </div>
        <s.SaqModalButtonsWrapper>
          <EiqButton 
            variant='btn--outline-link'
            label={t('CONTINUE_EDIT')}
            onClick={() => setShowModal(false)}
          />
          <EiqButton
            label={modalDetails?.buttonText} 
            onClick={async (e) => {
              e.preventDefault();
              try {
                if(modalDetails?.setJobStatus === 'verifier_review_pending' && isSaving){
                  setSubmitClicked(true);
                } else {
                  onChangeJobStatus(modalDetails?.setJobStatus);
                }
              } catch (error) {
                notification.error(error.graphQLErrors[0].message);
              }
            }}
          />
        </s.SaqModalButtonsWrapper>
        {isSaving && submitClicked && <s.Spinner><EiqSpinner /></s.Spinner>}
        </Popup>
      </s.SaqConfirmationModalWrapper>
      )}
    </>
  );
};

export default withRouter(withJobContext(NewSaqStatusesRow));
